import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  processUserCashbotQuery,
  addUserCashbotMessage,
  clearUserCashbotMessages,
  loadUserCashbotHistory,
  closeUserCashbot, 
  selectAdStatus, 
  checkAdStatus,
} from '../../../../app/store/slices/userDashboardSlice';
import { 
  acceptCashbotDisclaimer,
  selectCashbotDisclaimerAccepted,
  selectUser,
} from '../../../../app/store/slices/userSlice';
import { useCanvas } from '../../context/CanvasContext';
import { selectCurrentPlan } from '../../../../app/store/slices/subscriptionSlice';
import CashbotChat from '../../../shared/components/cashbot/components/CashbotChat';
import DisclaimerModal from '../../../shared/components/cashbot/components/DisclaimerModal';

const UserCashbotWrapper = () => {
  const dispatch = useDispatch();
  const dashboardState = useSelector(state => state.dashboard || {});
  const userDashboardState = useSelector(state => state.userDashboard || {});
  
  const cashbot = dashboardState.cashbot || userDashboardState.cashbot || { isOpen: false, messages: [], isTyping: false };
  const disclaimerAccepted = useSelector(selectCashbotDisclaimerAccepted);
  const currentUser = useSelector(selectUser);
  
  const { selectedCanvas } = useCanvas();
  const currentPlan = useSelector(selectCurrentPlan);
  const adStatus = useSelector(selectAdStatus);
  const canvasId = selectedCanvas?.id;
  
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  
  // Check if user has access based on subscription
  const isFreeUser = currentPlan?.plan?.planKey === 'free';
  const hasAccess = !isFreeUser || (adStatus?.hasAccess && cashbot?.isOpen);

  useEffect(() => {
    dispatch(checkAdStatus(selectedCanvas?.id));
  }, [dispatch, selectedCanvas?.id]);

  // Show disclaimer when cashbot opens if not accepted
  useEffect(() => {
    if (cashbot?.isOpen && !disclaimerAccepted && hasAccess) {
      setShowDisclaimer(true);
    }
  }, [cashbot?.isOpen, disclaimerAccepted, hasAccess]);

  // Load chat history when canvas changes
  useEffect(() => {
    if (canvasId && !isFreeUser && adStatus?.hasAccess && cashbot?.isOpen && disclaimerAccepted) {
      dispatch(clearUserCashbotMessages());
      
      const storageKey = `user_cashbot_${canvasId}_history`;
      const savedMessages = localStorage.getItem(storageKey);
      
      if (savedMessages) {
        try {
          const parsedMessages = JSON.parse(savedMessages);
          dispatch(loadUserCashbotHistory(parsedMessages));
        } catch (error) {
          console.error('Error loading chat history:', error);
        }
      } else {
        const welcomeMessage = {
          id: Date.now(),
          type: 'bot',
          content: `Welcome to CashBot! I can help you analyze your finances for the "${selectedCanvas?.name || 'current'}" canvas.\n\nRemember: All information provided is for educational purposes only, not financial advice.`,
          timestamp: new Date().toISOString(),
          isWelcome: true,
        };
        dispatch(addUserCashbotMessage(welcomeMessage));
      }
    }
  }, [canvasId, dispatch, cashbot?.isOpen, selectedCanvas?.name, isFreeUser, disclaimerAccepted, adStatus?.hasAccess]);
  
  // Save messages to localStorage when they change
  useEffect(() => {
    if (cashbot?.messages?.length > 0 && canvasId && !isFreeUser) {
      const storageKey = `user_cashbot_${canvasId}_history`;
      localStorage.setItem(storageKey, JSON.stringify(cashbot.messages));
    }
  }, [cashbot?.messages, canvasId, isFreeUser]);

  const handleAcceptDisclaimer = async () => {
    try {
      await dispatch(acceptCashbotDisclaimer()).unwrap();
      
      // Update localStorage authData
      const authData = JSON.parse(localStorage.getItem('authData') || '{}');
      if (authData.user) {
        authData.user.cashbotDisclaimerAccepted = true;
        authData.user.cashbotDisclaimerVersion = '1.0';
        localStorage.setItem('authData', JSON.stringify(authData));
      }
      
      setShowDisclaimer(false);
      
      if (canvasId) {
        const welcomeMessage = {
          id: Date.now(),
          type: 'bot',
          content: `Welcome to CashBot! I can help you analyze your finances for the "${selectedCanvas?.name || 'current'}" canvas.\n\nRemember: All information provided is for educational purposes only, not financial advice.`,
          timestamp: new Date().toISOString(),
          isWelcome: true,
        };
        dispatch(addUserCashbotMessage(welcomeMessage));
      }
    } catch (error) {
      console.error('Error accepting disclaimer:', error);
    }
  };
  
  const handleSendMessage = async (message) => {
    if (!disclaimerAccepted) {
      setShowDisclaimer(true);
      return;
    }

    if (!canvasId) {
      dispatch(addUserCashbotMessage({
        id: Date.now(),
        type: 'bot',
        content: 'Please select a canvas first to use CashBot.',
        timestamp: new Date().toISOString(),
        isError: true,
      }));
      return;
    }
    
    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: message,
      timestamp: new Date().toISOString(),
    };
    dispatch(addUserCashbotMessage(userMessage));
    
    dispatch(processUserCashbotQuery({ 
      query: message,
      canvasId
    }));
  };
  
  const handleClearHistory = () => {
    dispatch(clearUserCashbotMessages());
    if (canvasId) {
      const storageKey = `user_cashbot_${canvasId}_history`;
      localStorage.removeItem(storageKey);
      
      const welcomeMessage = {
        id: Date.now(),
        type: 'bot',
        content: `Welcome to CashBot! I can help you analyze your finances for the "${selectedCanvas?.name || 'current'}" canvas.\n\nRemember: All information provided is for educational purposes only, not financial advice.`,
        timestamp: new Date().toISOString(),
        isWelcome: true,
      };
      dispatch(addUserCashbotMessage(welcomeMessage));
    }
  };
  
  if (!hasAccess) {
    return null;
  }
  
  return (
    <>
      <DisclaimerModal
        open={showDisclaimer}
        onAccept={handleAcceptDisclaimer}
      />
      
      {disclaimerAccepted && (
        <CashbotChat
          messages={cashbot.messages || []}
          isTyping={cashbot.isTyping || false}
          isOpen={cashbot.isOpen}
          onSendMessage={handleSendMessage}
          onClose={() => dispatch(closeUserCashbot())}
          onClearHistory={handleClearHistory}
          context="user"
          canvasId={canvasId}
          canvasName={selectedCanvas?.name || ''}
        />
      )}
    </>
  );
};

export default UserCashbotWrapper;